import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <SEO title="Privacy Policy" description="Privacy policy and data handling practices for NinjaCopia." />
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
              Privacy Policy
            </h1>

            <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              Last updated: January 2025
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Information We Collect</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                  We collect information you provide directly to us, such as when you:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                  <li>Join our waitlist or newsletter</li>
                  <li>Contact us for support</li>
                  <li>Purchase our software</li>
                  <li>Use our software and services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send you technical notices and support messages</li>
                  <li>Respond to your comments and questions</li>
                  <li>Send you marketing communications (with your consent)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Information Sharing</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information with trusted third parties who assist us in operating our website, conducting our business, or servicing you.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Data Security</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Software Data Collection</h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-4">
                  <p className="text-blue-800 dark:text-blue-200 font-semibold mb-2">
                    NinjaCopia Software Privacy
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    NinjaCopia does not collect, store, or transmit your trading data, account information, or personal trading strategies. All trade copying happens locally on your machine. We may collect anonymous usage statistics to improve the software.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Cookies and Tracking</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Our website uses cookies to enhance your experience. You can choose to disable cookies through your browser settings, but this may affect the functionality of our website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Email Communications</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  If you join our waitlist or newsletter, we may send you updates about NinjaCopia, including product announcements and educational content. You can unsubscribe at any time using the link provided in our emails.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Third-Party Services</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                  We use the following third-party services:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                  <li><strong>ConvertKit:</strong> For email marketing and newsletter management</li>
                  <li><strong>Supabase:</strong> For database services and user data storage</li>
                  <li><strong>Stripe:</strong> For payment processing (when available)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Your Rights</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Request data portability</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Contact Us</h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at: <br />
                  <span className="text-blue-600 dark:text-blue-400">support@ninjacopia.com</span>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;